import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { InputMode, Macro } from '@/common/model';
import type { MatchPerspective } from '../types';
import type { CourtViewRotation } from '../utils/courtLayout';
import { NEXT_ROTATION, horizontalRotationFor, isFlippedRotation, isHorizontalLayout } from '../utils/courtLayout';

export type TeamAdversairesFilter = 'all' | 'faced' | 'scouted';

interface SidebarState {
  isOpen: boolean;
  isDesktop: boolean;
}

interface InterfaceState {
  currentMode: InputMode;
  macros: Macro[];
  isMacroPanelOpen: boolean;
  sidebar: SidebarState;
  courtViewRotation: CourtViewRotation;
  expertCourtFlipped: boolean;
  isFeedbackOpen: boolean;
  /**
   * Live point history panel. In the store, not in the scoreboard: the « Match
   * terminé » window opens it too, to undo a misattributed match point (VOL-208).
   * Not persisted.
   */
  isHistoryPanelOpen: boolean;
  scoutingPerspectiveByMatchId: Record<string, MatchPerspective>;
  teamAdversairesFilter: TeamAdversairesFilter;

  setMode: (mode: InputMode) => void;
  toggleMacroPanel: () => void;
  addMacro: (macro: Macro) => void;
  removeMacro: (id: string) => void;
  /**
   * Puts a deleted macro back at its former place (the panel's undo): before the macro that
   * followed it, when still there, else at its former index.
   */
  restoreMacro: (macro: Macro, index: number, nextId?: string | null) => void;
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  setSidebarDesktop: (isDesktop: boolean) => void;
  setCourtViewRotation: (rotation: CourtViewRotation) => void;
  /**
   * Rotation suivante. `horizontalOnly` restreint le cycle aux deux orientations
   * horizontales (90°/270°), seules valides en saisie deux équipes — les deux
   * moitiés de terrain doivent rester côte à côte comme en mode expert.
   */
  cycleCourtViewRotation: (horizontalOnly?: boolean) => void;
  toggleExpertCourtFlipped: () => void;
  setFeedbackOpen: (open: boolean) => void;
  setHistoryPanelOpen: (open: boolean) => void;
  setScoutingMatchPerspective: (matchId: string, perspective: MatchPerspective) => void;
  setTeamAdversairesFilter: (filter: TeamAdversairesFilter) => void;
}

const DESKTOP_BREAKPOINT = 1024;

const ROUTES_SIDEBAR_CLOSED_BY_DEFAULT = ['/stats', '/trajectories'];

function getInitialSidebarState(): SidebarState {
  const isDesktop = typeof window !== 'undefined' && window.innerWidth >= DESKTOP_BREAKPOINT;
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
  const shouldStartClosed = ROUTES_SIDEBAR_CLOSED_BY_DEFAULT.some(r => pathname === r);
  return {
    isOpen: isDesktop && !shouldStartClosed,
    isDesktop
  };
}

const INTERFACE_STORAGE_VERSION = 1;

/**
 * v1 drops the widget layout and its edit mode, unused since the builder view
 * was removed (VOL-208); macros and court orientation are kept.
 */
export function migrateInterfaceStorage(persisted: unknown, version: number): Partial<InterfaceState> {
  const state: Record<string, unknown> = typeof persisted === 'object' && persisted !== null ? { ...persisted } : {};
  if (version < 1) {
    delete state.layout;
    delete state.isEditMode;
  }
  return state as Partial<InterfaceState>;
}

export const useInterfaceStore = create<InterfaceState>()(
  persist(
    (set) => ({
      currentMode: 'standard',
      macros: [],
      isMacroPanelOpen: false,
      sidebar: getInitialSidebarState(),
      courtViewRotation: 0,
      expertCourtFlipped: false,
      isFeedbackOpen: false,
      isHistoryPanelOpen: false,
      scoutingPerspectiveByMatchId: {},
      teamAdversairesFilter: 'all',

      setMode: (mode) => set({ currentMode: mode }),
      toggleMacroPanel: () => set((state) => ({ isMacroPanelOpen: !state.isMacroPanelOpen })),

      addMacro: (macro) => set((state) => ({ macros: [...state.macros, macro] })),
      removeMacro: (id) => set((state) => ({ macros: state.macros.filter(m => m.id !== id) })),
      restoreMacro: (macro, index, nextId) => set((state) => {
        if (state.macros.some((m) => m.id === macro.id)) return state;
        const macros = [...state.macros];
        // Two deletions undone in reverse order would swap them if only the index were kept.
        const nextIndex = nextId ? macros.findIndex((m) => m.id === nextId) : -1;
        const at = nextIndex >= 0 ? nextIndex : Math.min(Math.max(index, 0), macros.length);
        macros.splice(at, 0, macro);
        return { macros };
      }),

      toggleSidebar: () => set((state) => ({
        sidebar: { ...state.sidebar, isOpen: !state.sidebar.isOpen }
      })),

      setSidebarOpen: (open) => set((state) => ({
        sidebar: { ...state.sidebar, isOpen: open }
      })),

      setSidebarDesktop: (isDesktop) => set((state) => {
        if (state.sidebar.isDesktop === isDesktop) {
          return state;
        }
        const wasDesktop = state.sidebar.isDesktop;
        const transitioningDesktopToMobile = wasDesktop && !isDesktop;
        return {
          sidebar: {
            ...state.sidebar,
            isDesktop,
            isOpen: transitioningDesktopToMobile ? false : state.sidebar.isOpen,
          },
        };
      }),

      // Orientation : `courtViewRotation` (standard) et `expertCourtFlipped` (expert)
      // décrivent la MÊME chose — de quel côté se trouve notre camp. On les garde donc
      // synchronisés dans les deux sens, sinon passer du mode standard au mode expert
      // faisait changer les équipes de côté.
      setCourtViewRotation: (rotation) =>
        set({ courtViewRotation: rotation, expertCourtFlipped: isFlippedRotation(rotation) }),
      cycleCourtViewRotation: (horizontalOnly = false) =>
        set((state) => {
          const current = state.courtViewRotation;
          // Restreint à l'horizontale : depuis une orientation verticale on rejoint
          // d'abord l'horizontale du MÊME côté (aucune équipe ne change de camp),
          // ensuite chaque appel échange les deux camps.
          const next = horizontalOnly
            ? horizontalRotationFor(
                isHorizontalLayout(current) ? !isFlippedRotation(current) : isFlippedRotation(current),
              )
            : NEXT_ROTATION[current];
          return { courtViewRotation: next, expertCourtFlipped: isFlippedRotation(next) };
        }),
      toggleExpertCourtFlipped: () =>
        set((state) => {
          const flipped = !state.expertCourtFlipped;
          return { expertCourtFlipped: flipped, courtViewRotation: horizontalRotationFor(flipped) };
        }),
      setFeedbackOpen: (open) => set({ isFeedbackOpen: open }),
      setHistoryPanelOpen: (open) => set({ isHistoryPanelOpen: open }),

      setScoutingMatchPerspective: (matchId, perspective) =>
        set((state) => ({
          scoutingPerspectiveByMatchId: {
            ...state.scoutingPerspectiveByMatchId,
            [matchId]: perspective,
          },
        })),

      setTeamAdversairesFilter: (teamAdversairesFilter) => set({ teamAdversairesFilter }),
    }),
    {
      name: 'volley-stats-interface',
      storage: createJSONStorage(() => localStorage),
      version: INTERFACE_STORAGE_VERSION,
      migrate: migrateInterfaceStorage,
      partialize: (state) => ({
        currentMode: 'standard' as InputMode,
        macros: state.macros,
        isMacroPanelOpen: state.isMacroPanelOpen,
        courtViewRotation: state.courtViewRotation,
      }),
      // `expertCourtFlipped` est dérivé de la rotation (même orientation) : on le
      // recalcule à la réhydratation plutôt que de persister deux fois la même chose.
      merge: (persisted, current) => {
        const merged = { ...current, ...(persisted as Partial<InterfaceState>) };
        return { ...merged, expertCourtFlipped: isFlippedRotation(merged.courtViewRotation) };
      },
    }
  )
);

